import React, { useState, useMemo, forwardRef, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { MarginProps, margin } from 'styled-system'

import { FixMeAny } from 'types/global'
import Icon from '../../Icon'
import Text from '../../Text'

import BaseTextInput from './../BaseTextInput'
import BoxlessTextInput, { BoxlessProps } from './../BoxlessTextInput'
import AddonIcon from './../AddonIcon'
import SimpleTextInput from './../SimpleTextInput'
import { BaseTextInputProps, TextInputVariant } from './../types'
import { capitalizeFirstLetter } from 'utils/string'

type TextInputIcon = {
  icon: string | string[]
  color: string
  onClick?: () => void
}

const TextInputVariants = {
  boxless: BoxlessTextInput,
  labeled: SimpleTextInput,
  simple: SimpleTextInput,
  unstyled: BaseTextInput,
}

const AddonWrapper: React.FC<{ position: 'before' | 'after'; children?: ReactNode }> = ({ position, children }) => {
  if (!children) return null

  if (typeof children === 'string') {
    return <AddonIcon position={position}>{children}</AddonIcon>
  }

  return <>{children}</>
}

export type TextInputProps = BaseTextInputProps &
  MarginProps & {
    variant?: TextInputVariant
    customIcon?: React.ReactNode
    addonAfter?: React.ReactNode
    addonBefore?: React.ReactNode
    hideInput?: boolean
    onClickIcon?: () => FixMeAny
  } & BoxlessProps

const TextInput = forwardRef<HTMLInputElement, TextInputProps>((incomingProps, ref) => {
  const {
    variant = 'simple',
    customIcon,
    addonAfter,
    addonBefore,
    hideInput,
    onClickIcon,
    customError,
    type = 'text',
    placeholder = 'please_enter_lbl',
    disabled = false,
    width = '100%',
    value = '',
    iconPosition = 'right',
    showError = true,
    ...rest
  } = incomingProps
  const baseProps = {
    ...rest,
    type,
    placeholder,
    disabled,
    width,
    value,
    iconPosition,
    showError,
  }
  const props = {
    ...baseProps,
    variant,
    customIcon,
    addonAfter,
    addonBefore,
    hideInput,
    onClickIcon,
    customError,
  }
  const { icon, iconColor } = baseProps
  const { t } = useTranslation()
  const [hidePassword, setHidePassword] = useState<boolean>(true)
  const TextInputComponent = TextInputVariants[variant]

  const hasLabel = variant === 'labeled' && props.label !== ''
  const marginProps: MarginProps = margin(props)

  const iconProps: TextInputIcon = useMemo(() => {
    let icnProps: TextInputIcon = {
      icon: '',
      color: props.disabled ? 'gray4' : iconColor ?? 'gray5',
      onClick: onClickIcon,
    }
    if (icnProps && props.type === 'password') {
      icnProps = {
        ...icnProps,
        icon: hidePassword ? 'eye' : 'eye-slash',
      }
    } else if (props.showWarning) {
      icnProps = {
        ...icnProps,
        icon: ['far', 'exclamation-triangle'],
        color: 'yellow',
      }
    } else if (icon) {
      icnProps = {
        ...icnProps,
        icon,
      }
    }
    return icnProps
  }, [hidePassword, icon, iconColor, onClickIcon, props.disabled, props.showWarning, props.type])

  const iconComponent = () => {
    if (props.type === 'password') {
      return (
        <IconWrapper iconPosition={iconPosition} hasOnClick={!!onClickIcon}>
          <PasswordButton type="button" onClick={() => setHidePassword(!hidePassword)}>
            <Icon {...iconProps} />
          </PasswordButton>
        </IconWrapper>
      )
    }

    if (customIcon)
      return (
        <IconWrapper iconPosition={iconPosition} hasOnClick={!!onClickIcon}>
          {customIcon}
        </IconWrapper>
      )

    if (!iconProps.icon) return null

    return (
      <IconWrapper iconPosition={iconPosition} hasOnClick={!!onClickIcon}>
        <Icon {...iconProps} />
      </IconWrapper>
    )
  }

  const updatedProps = useMemo(
    () =>
      ({
        ...baseProps,
        value: (props.readOnly || props.disabled) && !props.value ? '-' : props.value,
        hidePassword,
      } as BaseTextInputProps),
    [baseProps, props.disabled, props.readOnly, props.value, hidePassword],
  )

  const errorComponent = customError ? (
    customError
  ) : (
    <ErrorMessage marginLeft="8px" errorVariant={props.errorVariant} isCapitalizedFirstLetter>
      {props.error}
    </ErrorMessage>
  )

  return (
    <Container inline={props.inline} width={width} {...marginProps}>
      {hasLabel && props.label && (
        <LabelText disabled={props.disabled} readOnly={props.readOnly}>
          {capitalizeFirstLetter(t(props.label as string))}
          {props.required && <RequiredText>*</RequiredText>}
        </LabelText>
      )}

      <InputBox type={props.type} height={baseProps.height}>
        <AddonWrapper position="before">{addonBefore}</AddonWrapper>
        <Wrapper>
          {iconPosition === 'left' && iconComponent()}
          {!hideInput && <TextInputComponent ref={ref} {...updatedProps} />}
          {iconPosition === 'right' && iconComponent()}
        </Wrapper>
        <AddonWrapper position="after">{addonAfter}</AddonWrapper>
      </InputBox>

      {props.error && props.showError ? errorComponent : props.hint ? <HintMessage>{props.hint}</HintMessage> : null}
    </Container>
  )
})

const Container = styled.div<{ inline?: boolean; width?: string | number } & MarginProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 2px;
  ${margin}
  
/* 
  * {
    box-sizing: border-box;
  }

  display: ${(p) => (p.inline ? 'inline-block' : 'block')};
  position: relative;
  ${(p) => css`
    width: ${p.width === 'auto' ? `100%` : typeof p.width === 'string' ? p.width : `${p.width}px`};
  `} */
`

const InputBox = styled.div<{ type: BaseTextInputProps['type']; height?: string | number }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  ${(p) =>
    p.type === 'textarea'
      ? ''
      : `height: ${p.height ? (typeof p.height === 'string' ? p.height : `${p.height}px`) : '40px'};`}
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;

  &:first-child input,
  &:first-child textarea {
    border-radius: 4px 0 0 4px;
  }

  &:last-child input,
  &:last-child textarea {
    border-radius: 0 4px 4px 0;
  }

  &:only-child input,
  &:only-child textarea {
    border-radius: 4px;
  }
`

const IconWrapper = styled.div<{
  iconPosition: BaseTextInputProps['iconPosition']
  hasOnClick?: boolean
}>`
  z-index: 1;
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  ${(p) =>
    p.iconPosition === 'left'
      ? css`
          left: 1rem;
        `
      : css`
          right: 1rem;
        `}
  ${(p) =>
    p.hasOnClick &&
    css`
      cursor: pointer;
    `}
`

export const LabelText = styled.div<{ disabled?: boolean; readOnly?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  line-height: 21px;
  ${(p) => p.theme.font({ color: p.disabled ? 'gray4' : p.readOnly ? 'gray5' : 'defaultText' })}
`

export const RequiredText = styled.span`
  color: ${(p) => p.theme.colors.red};
  margin-left: 4px;
`

const BaseMessage = styled.span`
  ${(p) => p.theme.font()}
  bottom: -20px;
  position: absolute;
`

export const ErrorMessage = styled(Text)<{ errorVariant?: 'warning' | 'error' }>`
  ${(p) => p.theme.font({ size: 14, color: p.errorVariant === 'warning' ? 'yellow' : 'red', weight: 'medium' })}
  margin: 8px 0 0;
  text-align: left;
  white-space: pre-line;
`

export const HintMessage = styled(BaseMessage)`
  color: ${(p) => p.theme.colors.defaultText};
`

const PasswordButton = styled.button`
  padding: 0;
`

export default TextInput
